import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { errorLogKeys } from "@/lib/query";
import type { ErrorSource, ErrorStatus, ErrorView } from "@/schemas/error-log";

export const ERROR_LOG_PAGE_SIZE = 25;

/**
 * One captured error, grouped by fingerprint. (Backend twin: api/error-log.ts.)
 */
export interface ErrorLogRow {
  id: string;
  message: string;
  source: ErrorSource;
  stack: string | null;
  componentStack: string | null;
  routePath: string | null;
  userAgent: string | null;
  /** How many times this error has fired, across all occurrences. */
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  status: ErrorStatus;
  fixedAt: string | null;
  /**
   * What the fix was — required to mark an error fixed. Survives a reopen, so on
   * an open/regressed row it's the record of an attempt that didn't hold.
   */
  fixNote: string | null;
  lastUserName: string | null;
  lastUserEmail: string | null;
  /**
   * Marked fixed, but has fired since. Derived server-side from
   * lastSeenAt vs fixedAt — such a row lists under `open`, not `fixed`.
   */
  regressed: boolean;
}

interface ErrorLogPage {
  data: ErrorLogRow[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface ErrorLogPageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

export function useErrorLog(
  filters: { view: ErrorView; search: string },
  pageRequest: ErrorLogPageRequest,
) {
  return useQuery({
    queryKey: [...errorLogKeys.list(filters), pageRequest],
    queryFn: async (): Promise<ErrorLogPage> => {
      const params = new URLSearchParams();
      params.set("limit", String(ERROR_LOG_PAGE_SIZE));
      params.set("view", filters.view);
      if (filters.search) params.set("search", filters.search);
      if (pageRequest.cursor) params.set("cursor", pageRequest.cursor);
      params.set("direction", pageRequest.direction);
      const res = await fetch(`/api/error-log?${params}`);
      if (!res.ok) throw new Error("Failed to load the error log");
      return res.json();
    },
  });
}

/**
 * Mark an error fixed, or put it back on the open list. Invalidates the whole
 * error-log tree — a status change moves the row between the two tabs, so
 * guessing which keys shifted is pointless.
 *
 * `fixNote` is required by the server when status is "fixed"; its message is
 * surfaced verbatim rather than paraphrased.
 */
export function useSetErrorStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      fixNote,
    }: {
      id: string;
      status: ErrorStatus;
      fixNote?: string;
    }) => {
      const res = await fetch(`/api/error-log/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, fixNote }),
      });
      if (!res.ok) {
        const body: unknown = await res.json().catch(() => null);
        const message =
          body !== null &&
          typeof body === "object" &&
          "error" in body &&
          typeof body.error === "string"
            ? body.error
            : "Failed to update the error";
        throw new Error(message);
      }
      return res.json();
    },
    onSuccess: (_data, { status }) => {
      qc.invalidateQueries({ queryKey: errorLogKeys.all });
      toast.success(status === "fixed" ? "Marked as fixed" : "Moved back to open");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to update the error"),
  });
}
